import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { api, AgentPendingAction } from "../api";

type ChatMsg = {
  role: "user" | "assistant" | "system";
  text: string;
  action?: AgentPendingAction | null;
};

export type ImportOrderAiChatHandle = {
  openAttach: () => void;
};

export type ImportOrderAiChatProps = {
  form: any;
  orderId: number | null;
  onFormPatch: (patch: any) => void;
  onOrderSaved?: (orderId: number) => void;
  onNeedReload?: () => void;
  /** Parent may stash original File for Drive upload on save. */
  onLocalFile?: (file: File) => void;
  /** Auto-open file picker once (deeplink ?action=scan). */
  autoOpenAttach?: boolean;
};

function actionSummary(a: AgentPendingAction): string {
  const raw = a as Record<string, any>;
  if (raw.summary) return String(raw.summary);
  switch (a.type) {
    case "create_import_order":
      return `Tạo đơn nhập · ${a.rows?.length || 0} dòng · ${a.supplier_name || "NCC"}`;
    case "create_product":
      return `Tạo/cộng tồn ${a.rows?.length || 0} SP`;
    case "map_import_items":
      return `Map ${a.rows?.length || 0} dòng đơn #${a.import_order_id}`;
    case "receive_import":
      return `Nhập kho đơn #${a.import_order_id}`;
    case "delete_import_order":
      return `Xóa đơn #${a.import_order_id}`;
    default:
      return `Hành động: ${String(raw.type || "?")}${raw.import_order_id != null ? ` · #${raw.import_order_id}` : ""}`;
  }
}

function PendingActionCard({
  action,
  onDone,
  onCancel,
}: {
  action: AgentPendingAction;
  onDone: (msg: string, orderId?: number) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.ai.applyAction(action);
      const msg = (r.ok ? "✅ " : "❌ ") + (r.message || (r.ok ? "Đã áp dụng" : "Thất bại"));
      let orderId: number | undefined;
      if (action.type === "create_import_order" && r.ok) {
        const m = String(r.message || "").match(/#(\d+)/);
        if (m) orderId = Number(m[1]);
      } else if ("import_order_id" in action && typeof (action as { import_order_id?: number }).import_order_id === "number") {
        orderId = (action as { import_order_id: number }).import_order_id;
      }
      onDone(msg, orderId);
    } catch (e: unknown) {
      setErr((e as Error)?.message || "Lỗi apply");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        borderRadius: 10,
        border: "1px solid rgba(167,139,250,0.35)",
        background: "rgba(167,139,250,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--text,#e8e8ef)" }}>
        Cần xác nhận
      </div>
      <div style={{ color: "var(--text-secondary,#aaa)", marginBottom: 8, lineHeight: 1.4 }}>
        {actionSummary(action)}
      </div>
      {err && (
        <div role="alert" style={{ color: "var(--accent-red,#f87171)", marginBottom: 6 }}>
          {err}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy}
          style={{
            flex: 1,
            minHeight: 40,
            borderRadius: 8,
            border: "none",
            cursor: busy ? "wait" : "pointer",
            background: "linear-gradient(135deg,#34d399,#10b981)",
            color: "#04231a",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {busy ? "Đang áp dụng…" : "Xác nhận"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          style={{
            minHeight: 40,
            minWidth: 88,
            borderRadius: 8,
            border: "1px solid var(--border,#2a2a38)",
            cursor: busy ? "wait" : "pointer",
            background: "transparent",
            color: "var(--text-secondary,#aaa)",
            fontSize: 13,
          }}
        >
          Hủy
        </button>
      </div>
    </div>
  );
}

const ImportOrderAiChat = forwardRef<ImportOrderAiChatHandle, ImportOrderAiChatProps>(
  function ImportOrderAiChat(
    { form, orderId, onFormPatch, onOrderSaved, onNeedReload, onLocalFile, autoOpenAttach },
    ref
  ) {
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<
      Array<{ base64: string; mime_type: string; name: string; preview?: string; raw?: File }>
    >([]);
    const [msgs, setMsgs] = useState<ChatMsg[]>([
      {
        role: "assistant",
        text: "Gửi nhiều ảnh/PDF (kéo thả hoặc dán) hoặc gõ lệnh chỉnh đơn (vd: sl 20 bán 35k). Form bên dưới cập nhật ngay — chưa lưu DB cho đến khi bạn bấm Lưu/Tạo.",
      },
    ]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const taRef = useRef<HTMLTextAreaElement>(null);
    const autoOpened = useRef(false);

    function resizeComposer() {
      const el = taRef.current;
      if (!el) return;
      el.style.height = "auto";
      // Cap ~6–8 rows; scroll inside textarea past this (never cover buttons)
      const max = 152;
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 52), max)}px`;
    }

    useEffect(() => {
      resizeComposer();
    }, [input]);

    useImperativeHandle(ref, () => ({
      openAttach: () => fileRef.current?.click(),
    }));

    useEffect(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [msgs, busy]);

    useEffect(() => {
      if (!autoOpenAttach || autoOpened.current) return;
      autoOpened.current = true;
      const t = setTimeout(() => fileRef.current?.click(), 300);
      return () => clearTimeout(t);
    }, [autoOpenAttach]);

    function pickFiles(list: FileList | File[]) {
      const MAX_EACH = 8 * 1024 * 1024;
      const MAX_FILES = 12;
      const incoming = Array.from(list);
      const skipped: string[] = [];
      const accepted: File[] = [];
      const room = MAX_FILES - pendingFiles.length;
      for (const file of incoming) {
        const mime = file.type || "";
        const isPdf = mime === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        const isImage = mime.startsWith("image/");
        if (!isPdf && !isImage) {
          skipped.push(`${file.name}: chỉ nhận ảnh hoặc PDF`);
          continue;
        }
        if (file.size > MAX_EACH) {
          skipped.push(`${file.name}: quá 8MB`);
          continue;
        }
        if (accepted.length >= room) {
          skipped.push(`${file.name}: tối đa ${MAX_FILES} file một lượt`);
          continue;
        }
        accepted.push(file);
      }
      if (skipped.length) alert(skipped.slice(0, 8).join("\n"));
      for (const file of accepted) {
        onLocalFile?.(file);
        const mime = file.type || "application/octet-stream";
        const isImage = mime.startsWith("image/");
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = String(reader.result || "");
          const base64 = dataUrl.split(",")[1] || "";
          setPendingFiles((cur) => {
            if (cur.length >= MAX_FILES) return cur;
            if (cur.some((p) => p.name === file.name && p.base64 === base64)) return cur;
            return [
              ...cur,
              { base64, mime_type: mime, name: file.name || "file", preview: isImage ? dataUrl : undefined, raw: file },
            ];
          });
        };
        reader.readAsDataURL(file);
      }
    }

    async function send() {
      const q = input.trim();
      const files = pendingFiles;
      if ((!q && files.length === 0) || busy) return;
      setInput("");
      setPendingFiles([]);
      // Last 20 user+assistant turns (exclude system + welcome). File turns → `[có file]` for API history.
      const history = msgs
        .filter((m) => m.role !== "system" && !m.text.startsWith("Gửi ảnh/PDF"))
        .slice(-20)
        .map((m) => {
          const role = m.role === "assistant" ? "assistant" : "user";
          const fileUi = m.text.match(/^📎\s*\[\d+\s*file\]\s*/);
          const content = fileUi
            ? `[có file] ${m.text.slice(fileUi[0].length)}`
            : m.text;
          return { role, content };
        });
      const defaultUserText = q || (files.length > 1 ? "Đọc các file này giúp tôi" : "Đọc file này giúp tôi");
      const fileLabel = files.length > 0 ? `📎 [${files.length} file] ` : "";
      const apiUserContent =
        files.length > 0 ? `[có file] ${defaultUserText}` : defaultUserText;
      setMsgs((m) => [...m, { role: "user", text: fileLabel + defaultUserText }]);
      setBusy(true);
      try {
        const filesPayload =
          files.length > 0
            ? files.map((f) => ({ base64: f.base64, mime_type: f.mime_type, name: f.name }))
            : undefined;
        const r = await api.importOrders.aiChat({
          messages: [...history, { role: "user", content: apiUserContent }],
          form,
          order_id: orderId,
          files: filesPayload,
        });
        if (r.form_patch) onFormPatch(r.form_patch);
        let assistantText = r.assistant_text || "Đã xử lý.";
        if (r.tool_trace && r.tool_trace.length > 0) {
          const tools = r.tool_trace.join(", ");
          const nItems = Array.isArray(r.form_patch?.items) ? r.form_patch.items.length : 0;
          const formPart = nItems > 0 ? ` · ${nItems} dòng form` : "";
          assistantText += `\n(tools: ${tools}${formPart})`;
        }
        setMsgs((m) => [
          ...m,
          {
            role: "assistant",
            text: assistantText,
            action: r.pendingAction || undefined,
          },
        ]);
      } catch (e: unknown) {
        setMsgs((m) => [
          ...m,
          {
            role: "assistant",
            text: "❌ " + ((e as Error)?.message || "Lỗi chat AI. Kiểm tra cấu hình AI hoặc thử lại."),
          },
        ]);
      } finally {
        setBusy(false);
      }
    }

    function clearActionAt(idx: number) {
      setMsgs((m) => m.map((msg, i) => (i === idx ? { ...msg, action: undefined } : msg)));
    }

    return (
      <div
        role="region"
        aria-label="Chat AI đơn nhập"
        onDragOver={(e) => {
          if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
        }}
        onDrop={(e) => {
          if (!e.dataTransfer?.files?.length) return;
          e.preventDefault();
          pickFiles(e.dataTransfer.files);
        }}
        style={{
          width: "100%",
          minWidth: 0,
          borderRadius: 12,
          border: "1px solid rgba(167,139,250,0.35)",
          background: "linear-gradient(135deg, rgba(167,139,250,0.12) 0%, rgba(255,255,255,0.02) 100%)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 220,
          maxHeight: 420,
        }}
      >
        <div
          style={{
            padding: "10px 12px",
            borderBottom: "1px solid rgba(167,139,250,0.2)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span className="material-symbols-outlined" style={{ color: "var(--accent-ai,#a78bfa)", fontSize: 22 }}>
            auto_awesome
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>AI đơn nhập</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.35 }}>
              Nhiều ảnh/PDF một lượt + chỉnh form
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          style={{
            flex: "1 1 0%",
            overflowY: "auto",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {msgs.map((m, i) => (
            <div
              key={i}
              style={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                maxWidth: m.action ? "100%" : "92%",
                minWidth: 0,
                width: m.action ? "100%" : undefined,
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 12,
                  fontSize: 13,
                  lineHeight: 1.45,
                  whiteSpace: "pre-wrap",
                  background:
                    m.role === "user"
                      ? "linear-gradient(135deg,#a78bfa,#7c5cff)"
                      : m.role === "system"
                        ? "rgba(52,211,153,0.1)"
                        : "var(--surface-2,#24242f)",
                  color: m.role === "user" ? "#fff" : "var(--text,#e8e8ef)",
                  border: m.role === "system" ? "1px solid rgba(52,211,153,0.3)" : undefined,
                }}
              >
                {m.text}
              </div>
              {m.action && (
                <PendingActionCard
                  action={m.action}
                  onCancel={() => {
                    clearActionAt(i);
                    setMsgs((prev) => [...prev, { role: "system", text: "Đã hủy hành động." }]);
                  }}
                  onDone={(msg, oid) => {
                    clearActionAt(i);
                    setMsgs((prev) => [...prev, { role: "system", text: msg }]);
                    if (oid != null) onOrderSaved?.(oid);
                    onNeedReload?.();
                  }}
                />
              )}
            </div>
          ))}
          {busy && (
            <div style={{ fontSize: 12, color: "var(--text-tertiary,#888)" }}>Đang xử lý…</div>
          )}
        </div>

        <div
          style={{
            padding: 10,
            borderTop: "1px solid rgba(167,139,250,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            width: "100%",
            minWidth: 0,
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        >
          {pendingFiles.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                overflowX: "auto",
                width: "100%",
                minWidth: 0,
                paddingBottom: 2,
              }}
            >
              {pendingFiles.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  title={f.name}
                  style={{
                    position: "relative",
                    flex: "0 0 auto",
                    width: 52,
                    height: 52,
                  }}
                >
                  {f.preview ? (
                    <img
                      src={f.preview}
                      alt={f.name}
                      style={{
                        width: 52,
                        height: 52,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid var(--border,#2a2a38)",
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        width: 52,
                        height: 52,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        background: "var(--surface-2,#24242f)",
                        border: "1px solid var(--border,#2a2a38)",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      PDF
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={`Bỏ ${f.name}`}
                    onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      border: "none",
                      background: "#3a2030",
                      color: "#f87171",
                      cursor: "pointer",
                      fontSize: 12,
                      lineHeight: "18px",
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              width: "100%",
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            <input
              ref={fileRef}
              type="file"
              style={{ display: "none" }}
              accept="image/*,application/pdf"
              multiple
              onChange={(e) => {
                const f = e.target.files;
                if (f && f.length) pickFiles(f);
                e.currentTarget.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              title="Gửi nhiều ảnh/PDF"
              aria-label="Đính kèm ảnh hoặc PDF"
              style={{
                flex: "0 0 44px",
                width: 44,
                minWidth: 44,
                maxWidth: 44,
                minHeight: 44,
                flexShrink: 0,
                borderRadius: 9,
                border: "1px solid var(--border,#2a2a38)",
                cursor: busy ? "wait" : "pointer",
                background: "var(--surface-2,#24242f)",
                color: pendingFiles.length > 0 ? "#a78bfa" : "var(--text-secondary,#aaa)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                attach_file
              </span>
            </button>
            <textarea
              ref={taRef}
              value={input}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter = newline; Ctrl/Cmd+Enter = send
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  void send();
                }
              }}
              onPaste={(e) => {
                const list = e.clipboardData?.files;
                if (!list?.length) return;
                const hasDoc = Array.from(list).some((f) => f.type.startsWith("image/") || f.type === "application/pdf");
                if (!hasDoc) return;
                e.preventDefault();
                pickFiles(list);
              }}
              placeholder="Gõ danh sách SP, kéo thả hoặc dán nhiều ảnh (Ctrl+Enter gửi)…"
              title="Enter xuống dòng · Ctrl+Enter (⌘+Enter) gửi"
              disabled={busy}
              aria-label="Tin nhắn AI đơn nhập. Enter xuống dòng, Ctrl+Enter gửi"
              style={{
                flex: "1 1 0%",
                minWidth: 0,
                width: 0,
                minHeight: 52,
                maxHeight: 152,
                padding: "9px 12px",
                borderRadius: 9,
                border: "1px solid var(--border,#2a2a38)",
                background: "var(--surface-2,#24242f)",
                color: "var(--text,#e8e8ef)",
                fontSize: 13,
                lineHeight: 1.4,
                resize: "none",
                overflowY: "auto",
                overflowX: "hidden",
                wordBreak: "break-word",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy || (!input.trim() && pendingFiles.length === 0)}
              aria-label="Gửi"
              title="Ctrl+Enter gửi"
              style={{
                flex: "0 0 44px",
                width: 44,
                minWidth: 44,
                maxWidth: 44,
                minHeight: 44,
                flexShrink: 0,
                borderRadius: 9,
                border: "none",
                cursor: busy ? "wait" : "pointer",
                background: "linear-gradient(135deg,#a78bfa,#7c5cff)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                send
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default ImportOrderAiChat;
